// Storiz brand monogram + wordmark — gold mountain-in-open-book mark with a
// serif "Storiz" wordmark. Used across the admin sidebar, auth pages, and
// anywhere we want the brand to read instantly. Pure inline SVG so it
// renders at any size without a network fetch and inherits color via
// `currentColor` on the stroke when needed.
//
// Default colour is the canonical BRAND.gold (#C9A24A). Pass `className`
// to size the wrapper; the SVG scales to fit its container.

interface StorizMarkProps {
  className?: string;
  /** Show the wordmark alongside the mark. Defaults to false (mark only). */
  withWordmark?: boolean;
  /** Override the mark colour. Defaults to brand gold. */
  color?: string;
  /** Override the wordmark text colour. Defaults to charcoal #1F2937. */
  wordmarkColor?: string;
  /** Optional tagline shown below the wordmark in muted text. */
  tagline?: string;
  /** Override the tagline colour. Defaults to the charcoal at 50 %. */
  taglineColor?: string;
}

export function StorizMark({
  className = 'h-8',
  withWordmark = false,
  color = '#C9A24A',
  wordmarkColor = '#1F2937',
  tagline,
  taglineColor = 'rgba(31, 41, 55, 0.5)',
}: StorizMarkProps) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <svg
        viewBox="0 0 100 92"
        fill="none"
        className="h-full w-auto shrink-0"
        aria-hidden="true"
      >
        <g
          stroke={color}
          strokeWidth={5}
          strokeLinejoin="round"
          strokeLinecap="round"
          fill="none"
        >
          {/* Outer book frame */}
          <path d="M14 12 L86 12 L86 68 L14 68 Z" />
          {/* Frame side extensions — the open-book "ears" on each corner */}
          <path d="M6 12 L14 12 M14 24 L6 24" />
          <path d="M94 12 L86 12 M86 24 L94 24" />
          <path d="M6 68 L14 68 M14 56 L6 56" />
          <path d="M94 68 L86 68 M86 56 L94 56" />
          {/* Two mountains rising inside the frame */}
          <path d="M22 60 L38 32 L50 48 L66 24 L80 60" />
          {/* Open book pages curving outward at the base */}
          <path d="M14 68 Q30 85 50 78 Q70 85 86 68" />
        </g>
      </svg>
      {withWordmark && (
        <span className="flex flex-col leading-none">
          <span
            style={{
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              color: wordmarkColor,
              fontWeight: 500,
              fontSize: '1.15em',
              letterSpacing: '-0.01em',
            }}
          >
            Storiz
          </span>
          {tagline && (
            <span
              className="mt-0.5"
              style={{
                color: taglineColor,
                fontSize: '0.55em',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >
              {tagline}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
